/**
 * What the settings modal edits before Save (TV-17): numbers stay the text
 * typed in their fields until they are checked.
 */
import { INPUT_TYPE, PLOT_TYPE } from "../../engine/indicators/indicatorTypes";
import { INTEGER_PRECISION } from "../../engine/indicators/definitionHelpers";

const DECIMAL_COMMA = ",";
const DECIMAL_POINT = ".";
const MINUS = "-";
const NOT_DIGITS = /\D/g;
// Characters a number can be typed with; the rest is dropped before the sign is checked
const NOT_NUMBER_CHARACTERS = /[^\d.-]/g;
const MAX_LEVEL_DECIMALS = 8;

/**
 * Rules of a level without limits (indicators without `levelRules`, drawings):
 * any plain decimal number, a minus allowed.
 */
export const FREE_LEVEL_RULES = { min: -Infinity, max: Infinity, precision: MAX_LEVEL_DECIMALS };

/**
 * @param {string|number} text
 * @param {number} fallback - when the text is not a number
 * @returns {number}
 */
const parseNumber = (text, fallback) => {
  const trimmed = String(text).trim().replace(DECIMAL_COMMA, DECIMAL_POINT);
  const number = Number(trimmed);

  return trimmed && Number.isFinite(number) ? number : fallback;
};

/**
 * The text of a number field while it is typed, as in PO: only the allowed
 * characters (digits, a dot when there are decimals, a leading minus when
 * `min` is below zero; a comma becomes a dot), decimals beyond `precision`
 * cut, a number above `max` replaced by `max`. "", "-" and numbers below
 * `min` stay as typed until the field loses focus.
 *
 * @param {{ min: number, max: number, precision: number }} rules - a registry input or levelRules
 * @param {string} text
 * @returns {string}
 */
export const filterNumberText = (rules, text) => {
  const raw = String(text).replace(DECIMAL_COMMA, DECIMAL_POINT);
  const hasDecimals = rules.precision > INTEGER_PRECISION;
  // PO strips the minus only in whole-number fields that cannot be negative
  const allowsMinus = rules.min < 0 || hasDecimals;
  const sign = allowsMinus && raw.replace(NOT_NUMBER_CHARACTERS, "").startsWith(MINUS) ? MINUS : "";
  // A whole number drops the dot like any other character: "2.5" → "25"
  const [whole, ...rest] = hasDecimals ? raw.split(DECIMAL_POINT) : [raw];
  const digits = whole.replace(NOT_DIGITS, "");
  const decimals = rest.join("").replace(NOT_DIGITS, "").slice(0, rules.precision);
  const filtered = `${sign}${digits}${rest.length > 0 ? DECIMAL_POINT + decimals : ""}`;

  return Number(filtered) > rules.max ? String(rules.max) : filtered;
};

/**
 * A number field once it loses focus (or on Save), as in PO: the default
 * when the text is empty or not a number, `min` when below it, otherwise the
 * number within `max` with at most `precision` decimals.
 *
 * @param {{ min: number, max: number, precision: number }} rules - a registry input or levelRules
 * @param {string|number} text
 * @param {number} defaultValue
 * @returns {number}
 */
export const normalizeNumberInput = (rules, text, defaultValue) => {
  const number = parseNumber(filterNumberText(rules, text), defaultValue);
  const bounded = Math.min(rules.max, Math.max(rules.min, number));

  return Number(bounded.toFixed(rules.precision));
};

/**
 * Default value of a level of the definition (PO), 0 for a level the definition has not.
 *
 * @param {object} definition
 * @param {number} index
 * @returns {number}
 */
export const levelDefault = (definition, index) => definition.levels[index]?.value ?? 0;

/** Whether the Styles tab gives a plot a width: lines and points, not columns or backgrounds. */
export const hasPlotWidth = (plot) => plot.type !== PLOT_TYPE.HISTOGRAM && plot.type !== PLOT_TYPE.FILL;

/**
 * @param {{ inputs: object, styles: { plots: object, levels: object[] } }} indicator - engine.getIndicators() item
 * @param {object} definition - from the registry
 * @returns {{ inputs: object, plots: object, levels: object[] }}
 */
export const createDraft = (indicator, definition) => ({
  inputs: Object.fromEntries(
    definition.inputs.map(({ key, type }) => {
      const value = indicator.inputs[key];

      return [key, type === INPUT_TYPE.NUMBER ? String(value) : value];
    }),
  ),
  plots: Object.fromEntries(
    Object.entries(indicator.styles.plots).map(([key, style]) => [key, { ...style }]),
  ),
  levels: indicator.styles.levels.map((level) => ({ ...level, value: String(level.value) })),
});

/**
 * Changes for engine.updateIndicator: every input and style of the draft;
 * a number input that is not a number gets its default; so does a level of
 * an indicator with `levelRules`, other levels keep the saved value.
 *
 * @param {object} draft - createDraft
 * @param {object} indicator - engine.getIndicators() item
 * @param {object} definition
 * @returns {{ inputs: object, styles: { plots: object, levels: object[] } }}
 */
export const draftToChanges = (draft, indicator, definition) => ({
  inputs: Object.fromEntries(
    definition.inputs.map((input) => {
      const value = draft.inputs[input.key];

      return [
        input.key,
        input.type === INPUT_TYPE.NUMBER ? normalizeNumberInput(input, value, input.defaultValue) : value,
      ];
    }),
  ),
  styles: {
    plots: draft.plots,
    levels: draft.levels.map((level, index) => ({
      ...level,
      value: definition.levelRules
        ? normalizeNumberInput(definition.levelRules, level.value, levelDefault(definition, index))
        : parseNumber(level.value, indicator.styles.levels[index].value),
    })),
  },
});
